import { useEffect, useRef, useState } from "react";
import { Button, IconButton } from "@mui/material";
import { useStyle } from "./style";
import { GarlleryIcon } from "../icons/gallery-icon";
import { useTranslation } from "react-i18next";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

export type GoMakeImageFiledProps = {
  selectedNameFile?: string | null;
  onChange: (value: string | null, file?: File | null) => void;
  uploadButtonLabelKey?: string;
  /** When true, render PDF blobs / data URLs / *.pdf URLs in an iframe. */
  usePdfPreview?: boolean;
  acceptedFileTypes?: string;
};

function shouldShowPdfFrame(
  usePdfPreview: boolean | undefined,
  src: string
): boolean {
  if (!usePdfPreview || !src?.trim()) return false;
  const s = src.trim();
  if (/^data:application\/pdf/i.test(s)) return true;
  if (/^blob:/i.test(s)) return true;
  if (/\.pdf($|\?|#)/i.test(s)) return true;
  return false;
}

export const GoMakeImageFiled = ({
  selectedNameFile,
  onChange,
  uploadButtonLabelKey,
  usePdfPreview,
  acceptedFileTypes,
}: GoMakeImageFiledProps) => {
  const { classes } = useStyle();
  const { primaryColor } = useGomakeTheme();
  const { t } = useTranslation();
  const dir = t("direction");
  const isLtr = dir === "ltr";
  const localBlobRef = useRef<string | null>(null);
  const [selectedFileNameInGoMakeFiled, setSelectedFileNameInGoMakeFiled] =
    useState(selectedNameFile || "");
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const s =
      typeof selectedNameFile === "string" ? selectedNameFile.trim() : "";
    setSelectedFileNameInGoMakeFiled(selectedNameFile || "");
    setImagePreview(usePdfPreview && !shouldShowPdfFrame(usePdfPreview, s) ? "" : s);
    if (
      typeof selectedNameFile !== "string" ||
      selectedNameFile.length === 0 ||
      typeof localBlobRef.current !== "string"
    ) {
      return;
    }
    if (
      !selectedNameFile.startsWith("blob:") &&
      localBlobRef.current.startsWith("blob:")
    ) {
      URL.revokeObjectURL(localBlobRef.current);
      localBlobRef.current = null;
    }
  }, [selectedNameFile, usePdfPreview]);

  const revokeLocalBlob = () => {
    if (localBlobRef.current) {
      URL.revokeObjectURL(localBlobRef.current);
      localBlobRef.current = null;
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    revokeLocalBlob();
    setSelectedFileNameInGoMakeFiled("");
    setImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onChange(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFileNameInGoMakeFiled(file.name);

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (usePdfPreview && !isPdf) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFileNameInGoMakeFiled("");
      return;
    }

    if (isPdf) {
      revokeLocalBlob();
      const objectUrl = URL.createObjectURL(file);
      localBlobRef.current = objectUrl;
      setImagePreview(objectUrl);
      onChange(objectUrl, file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setImagePreview(result);
        onChange(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const showPdf = shouldShowPdfFrame(usePdfPreview, imagePreview);
  const showSavedPdfMarker =
    !!usePdfPreview && !!selectedFileNameInGoMakeFiled && !imagePreview;

  return (
    <div style={classes.inputContainer}>
      <div style={classes.fileInputStyle}>
        {imagePreview || selectedFileNameInGoMakeFiled ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            {showPdf ? (
              <iframe
                src={imagePreview}
                title={selectedFileNameInGoMakeFiled || "pdf"}
                style={{
                  border: "none",
                  width: 72,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: 4,
                  backgroundColor: "var(--theme-bg-secondary, #f8fafc)",
                }}
              />
            ) : showSavedPdfMarker ? (
              <GarlleryIcon />
            ) : (
              <img src={imagePreview} style={classes.imageContainerStyle} alt="" />
            )}
            <label
              style={{
                color: `var(--theme-primary-300, ${primaryColor(300)})`,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                flex: 1,
                minWidth: 0,
              }}
            >
              {selectedFileNameInGoMakeFiled}
            </label>
          </div>
        ) : (
          <>
            <GarlleryIcon />
            <label
              style={{
                color: `var(--theme-primary-300, ${primaryColor(300)})`,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
                textOverflow: "ellipsis",
              }}
            >
              {selectedFileNameInGoMakeFiled
                ? ""
                : t("documentingDesign.documnetCreation.uploadHere")}
            </label>
          </>
        )}
        <input
          ref={fileInputRef}
          placeholder={t("general.uploadFile")}
          onChange={handleFileSelect}
          accept={acceptedFileTypes ?? ".jpg, .jpeg, .png, .gif, .svg"}
          type="file"
          style={{ display: "none" }}
        />
        <div>
          <Button
            variant="contained"
            onClick={handleButtonClick}
            style={{
              backgroundColor: "var(--theme-primary-color, #ED028C)",
              height: "30px",
              marginRight: isLtr ? "3px" : "0",
              marginLeft: !isLtr ? "3px" : "0",
            }}
          >
            {t(uploadButtonLabelKey ?? "general.uploadLogo")}
          </Button>
          <IconButton onClick={handleClearFile} sx={{ fontSize: "1rem" }}>
            <FormatColorResetIcon
              sx={{ color: `var(--theme-primary-300, ${primaryColor(300)})` }}
            />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
